import * as Yup from 'yup';

import { maxText } from 'utils/validation';
import { getUploadedFile } from 'utils/helpers';
import { formatDate, formatCalendarDate } from 'utils/date';
import {
  IdObject,
  AccountingEvent,
  AccountingExpense,
  AccountingExpenseRentalDetail,
} from 'types';

import { FormValues } from './types';

export const eventInitialValues = {
  amount: undefined,
  eventId: undefined,
  percent: undefined,
};

// Owner ruling (H7, 2026-08-19), re-verified read-only on Production
// 2026-10-13: ExpenseCategories id 16 = "Car Rental", company 3. Categories
// are freeform per-company names with no type flag, so the id is the gate --
// a name match would be a guess about intent.
export const CAR_RENTAL_CATEGORY_ID = 16;

export const isCarRentalSelected = (categories: (number | string)[] = []) =>
  categories.some((id) => Number(id) === CAR_RENTAL_CATEGORY_ID);

// Mirrors enum_ExpenseRentalDetails_rentalCompany. The server upper-cases but
// refuses anything outside these four, so this is a select, not free text.
export const RENTAL_COMPANY_OPTIONS = [
  { value: 'ENTERPRISE', label: 'Enterprise' },
  { value: 'AVIS', label: 'Avis' },
  { value: 'BUDGET', label: 'Budget' },
  { value: 'OTHER', label: 'Other' },
];

// Column widths of ExpenseRentalDetails (renterName varchar(160),
// reservationNumber/agreementNumber varchar(120)).
const RENTER_NAME_MAX = 160;
const RENTAL_NUMBER_MAX = 120;

export const rentalDetailInitialValues = (
  data?: AccountingExpense['rentalDetail']
) => ({
  notes: data?.notes || '',
  renterName: data?.renterName || '',
  rentalCompany: data?.rentalCompany || '',
  agreementNumber: data?.agreementNumber || '',
  reservationNumber: data?.reservationNumber || '',
  // Booking date and agreement number are no longer entered (Owner, 13 Oct
  // 2026); an older rental's saved values are kept and sent back on edit.
  bookingDate: formatCalendarDate(data?.bookingDate),
});

// Owner, 18 Oct 2026: a car rental has ONE money input. The number typed is
// the estimate and is saved twice: as the expense amount (what reports, P&L
// and cashflow read) and as rentalDetail.estimatedAmountCents (the accrual
// estimate the QuickBooks rental lane reads). The statement step later
// PATCHes only the amount, so the two then differ and the rental is "final".
//   - estimate:    new, or still equal to its estimate -> "Estimated amount";
//                  saving keeps the two equal.
//   - final:       the amount moved away from the saved estimate -> "Final
//                  amount" with the estimate shown read-only; saving never
//                  touches the estimate.
//   - unestimated: a rental saved with no estimate (older rows) -> plain
//                  "Amount"; saving leaves the estimate empty.
export type CarRentalAmountState = 'final' | 'estimate' | 'unestimated';

export const CAR_RENTAL_AMOUNT_LABELS: Record<CarRentalAmountState, string> = {
  final: 'Final amount',
  unestimated: 'Amount',
  estimate: 'Estimated amount',
};

const dollarsToCents = (value: unknown) => {
  const text = String(value ?? '').trim();
  const number = Number(text);
  return text === '' || !Number.isFinite(number)
    ? undefined
    : Math.round(number * 100);
};

// The car rental's own part of the expense: the whole amount, or the Car
// Rental row of a category split.
const savedRentalCents = (data: AccountingExpense) =>
  data.categories?.length > 1
    ? dollarsToCents(
        data.categories.find(
          (category) => Number(category.id) === CAR_RENTAL_CATEGORY_ID
        )?.ExpenseCategoryLink?.amount
      )
    : dollarsToCents(data.amount);

const formRentalCents = (values: FormValues) =>
  values.categories.length > 1
    ? dollarsToCents(
        values.categoryAllocations?.find(
          (row) => Number(row.categoryId) === CAR_RENTAL_CATEGORY_ID
        )?.amount
      )
    : dollarsToCents(values.amount);

// Decided from the SAVED expense, so a final rental stays final while it is
// edited, and a new one stays an estimate while it is typed.
export const getCarRentalAmountState = (
  data?: AccountingExpense
): CarRentalAmountState => {
  if (!data) {
    return 'estimate';
  }
  const estimate = data.rentalDetail?.estimatedAmountCents;
  if (!data.rentalDetail) {
    // Saved as Car Rental before the rental fields existed: an older row
    // with no estimate. Re-categorised to Car Rental now: a new rental.
    return isCarRentalSelected(data.categories?.map((category) => category.id))
      ? 'unestimated'
      : 'estimate';
  }
  if (estimate == null) {
    return 'unestimated';
  }
  const saved = savedRentalCents(data);
  return saved !== undefined && saved !== Number(estimate)
    ? 'final'
    : 'estimate';
};

// SPL Shipment (owner 22 Oct 2026: "needs to be like car rental"): the same
// ONE money input and the same three amount states as a car rental. The
// typed amount is the estimate, saved as the expense amount and as
// Expenses.estimatedAmountCents; the SPL invoice later moves only the amount
// (the Control Center's Save to CLIX), so the two differ and the expense is
// "final". Unlike Car Rental (a fixed id, H7) the category is found BY NAME,
// as the QuickBooks worker resolves it (quickbooksSplSave.js
// resolveSplCategory: trimmed, case-insensitive, exactly one): its id
// differs per company and environment.
export const SPL_SHIPMENT_CATEGORY_NAME = 'SPL Shipment';

const isSplShipmentName = (name?: string | null) =>
  String(name ?? '')
    .trim()
    .toUpperCase() === SPL_SHIPMENT_CATEGORY_NAME.toUpperCase();

// The company's SPL Shipment category id: undefined when it has none, or
// more than one (then nothing on the form is treated as SPL, the same
// answer as the worker's "SPL saves wait").
export const getSplShipmentCategoryId = (
  categories?: { id: number | string; name?: string | null }[] | null
) => {
  const found = (categories || []).filter((category) =>
    isSplShipmentName(category.name)
  );
  return found.length === 1 ? Number(found[0].id) : undefined;
};

export const isSplShipmentSelected = (
  categories: (number | string)[] = [],
  splCategoryId?: number
) =>
  splCategoryId !== undefined &&
  categories.some((id) => Number(id) === splCategoryId);

// The saved expense's own SPL Shipment category (its categories carry their
// names on GET /accounting/expense/:id).
const savedSplCategory = (data?: AccountingExpense) =>
  data?.categories?.find((category) => isSplShipmentName(category.name));

// The SPL part of the expense: the whole amount, or the SPL row of a split.
const savedSplCents = (data: AccountingExpense) =>
  data.categories?.length > 1
    ? dollarsToCents(savedSplCategory(data)?.ExpenseCategoryLink?.amount)
    : dollarsToCents(data.amount);

const formSplCents = (values: FormValues, splCategoryId: number) =>
  values.categories.length > 1
    ? dollarsToCents(
        values.categoryAllocations?.find(
          (row) => Number(row.categoryId) === splCategoryId
        )?.amount
      )
    : dollarsToCents(values.amount);

// Decided from the SAVED expense, exactly like getCarRentalAmountState.
export const getSplAmountState = (
  data?: AccountingExpense
): CarRentalAmountState => {
  if (!data) {
    return 'estimate';
  }
  const estimate = data.estimatedAmountCents;
  if (estimate == null) {
    // Saved as SPL Shipment with no estimate: an older row, or one the SPL
    // invoice created (final at birth). Re-categorised to SPL now: new.
    return savedSplCategory(data) ? 'unestimated' : 'estimate';
  }
  const saved = savedSplCents(data);
  return saved !== undefined && saved !== Number(estimate)
    ? 'final'
    : 'estimate';
};

// Builds the top-level `estimatedAmountCents` key of the create/edit
// request (main/src/services/accounting.js: present = save, null = clear,
// absent = leave it):
//   - SPL selected, estimate state -> the typed SPL amount, in cents;
//   - SPL selected, final or no estimate -> absent (never touched);
//   - SPL no longer selected on an expense that had an estimate -> null;
//   - otherwise -> absent.
// `splCategoryId` is the company's SPL Shipment category (the form adds
// it as values.splShipmentCategoryId); `saved` is the expense being edited.
export const getSplEstimatePayload = (
  values: FormValues,
  saved?: AccountingExpense
): { estimatedAmountCents?: number | null } => {
  const splCategoryId = values.splShipmentCategoryId;
  if (!isSplShipmentSelected(values.categories, splCategoryId)) {
    return saved?.estimatedAmountCents != null
      ? { estimatedAmountCents: null }
      : {};
  }
  if (getSplAmountState(saved) !== 'estimate') {
    return {};
  }
  const cents = formSplCents(values, splCategoryId as number);
  return cents === undefined || cents < 0
    ? {}
    : { estimatedAmountCents: cents };
};

// A saved money amount as the input shows it: always two decimals ("850.10",
// "1050.00"), whatever the category; blank when there is none. The API sends
// FLOATs (850.1), so a bare toString() dropped the trailing zero.
export const moneyInputValue = (value?: number | string | null) =>
  value === null ||
  value === undefined ||
  value === '' ||
  !Number.isFinite(Number(value))
    ? ''
    : Number(value).toFixed(2);

const getIdsArray = <T extends IdObject>(array?: T[]) =>
  array?.map((item) => item.id) || [];

const getFormattedDate = (date?: string) => formatCalendarDate(date);

const getEvents = (events?: AccountingEvent[], initialEventId?: number) =>
  events?.map((event) => ({
    amount: event.amount,
    eventId: event.eventId,
    percent: event.percent,
  })) || [{ ...eventInitialValues, percent: 100, eventId: initialEventId }];

export const getInitialValues = (
  data?: AccountingExpense,
  initialEventId?: number,
  employeeId?: number
): FormValues => ({
  receiptUrl: data?.receiptUrl || '',
  description: data?.description || '',
  amount: moneyInputValue(data?.amount),
  categories: getIdsArray(data?.categories),
  paidDate: getFormattedDate(data?.paidDate),
  receipt: getUploadedFile(data?.receiptUrl),
  events: getEvents(data?.events, initialEventId),
  paymentDate: getFormattedDate(data?.paymentDate),
  paymentMethodId: data?.paymentMethodId?.toString() || '',
  rentalDetail: rentalDetailInitialValues(data?.rentalDetail),
  employees: employeeId ? [employeeId] : getIdsArray(data?.employees),
  categoryAllocations:
    data?.categories?.map((category) => ({
      categoryId: category.id,
      amount:
        data.categories.length === 1
          ? moneyInputValue(data.amount)
          : String(category.ExpenseCategoryLink?.amount ?? ''),
    })) || [],
});

export const validationSchema = Yup.object().shape({
  paidDate: Yup.string(),
  paymentDate: Yup.string().required(),
  paymentMethodId: Yup.string().required(),
  employees: Yup.array().required().min(1),
  categories: Yup.array().required().min(1),
  description: maxText('Description').min(5),
  receipt: Yup.object().required('File is a required field'),
  amount: Yup.number().required().typeError('Amount must be a number'),
  // Renter and rental company are NOT NULL, all-or-nothing on the server;
  // they and the reservation number are required here only when Car Rental
  // is selected.
  rentalDetail: Yup.object().when('categories', {
    is: (categories: number[]) => isCarRentalSelected(categories),
    then: (schema) =>
      schema.shape({
        // No longer entered (Owner, 13 Oct 2026); an older rental's saved
        // value is kept and sent back unchanged on edit.
        agreementNumber: Yup.string().trim().max(RENTAL_NUMBER_MAX),
        rentalCompany: Yup.string().required(
          'Rental company is a required field'
        ),
        renterName: Yup.string()
          .trim()
          .max(RENTER_NAME_MAX)
          .required('Renter name is a required field'),
        // The rental company's booking confirmation (Owner, 13 Oct 2026).
        reservationNumber: Yup.string()
          .trim()
          .max(RENTAL_NUMBER_MAX)
          .required('Reservation number is a required field'),
      }),
  }),
  categoryAllocations: Yup.array().test(
    'category-split',
    'Enter an amount for every category; category amounts must total the expense amount',
    function (rows) {
      const { amount, categories } = this.parent;
      if (categories?.length <= 1) {
        return true;
      }
      if (!rows || rows.length !== categories.length) {
        return false;
      }
      const ids = rows.map((row) => Number(row.categoryId));
      const total = Math.round(Number(amount) * 100);
      return (
        new Set(ids).size === categories.length &&
        categories.every((id: number) => ids.includes(Number(id))) &&
        rows.every(
          (row) =>
            /^-?\d+(\.\d{1,2})?$/.test(String(row.amount)) &&
            Math.sign(Number(row.amount)) === Math.sign(total)
        ) &&
        rows.reduce(
          (sum, row) => sum + Math.round(Number(row.amount) * 100),
          0
        ) === total
      );
    }
  ),
  events: Yup.array()
    .of(
      Yup.object().shape({
        eventId: Yup.number().required('Event is a required field'),
        amount: Yup.number().required('Amount is a required field'),
        percent: Yup.number()
          .required('Percent is a required field')
          .min(0)
          .test(
            'percent-amount',
            'Please fill out the amount first',
            function () {
              return this?.options?.context?.amount;
            }
          ),
      })
    )
    .test(
      'total-percentage',
      'The total percentage must be 100%',
      function (events) {
        const totalPercentage = events?.reduce(
          (sum, event) => sum + (+event.percent || 0),
          0
        );

        return Math.round((totalPercentage || 0) * 100) === 10000;
      }
    )
    .test(
      'total-amount',
      'Event amounts must total the expense amount',
      function (events) {
        const expenseAmount = Number(this.parent.amount);
        if (
          !Number.isFinite(expenseAmount) ||
          !events?.length ||
          events.some(
            (event) =>
              event.amount == null || !Number.isFinite(Number(event.amount))
          )
        ) {
          return true;
        }
        const allocationCents = events.reduce(
          (sum, event) => sum + Math.round(Number(event.amount) * 100),
          0
        );
        return allocationCents === Math.round(expenseAmount * 100);
      }
    ),
});

export const getFormattedDates = (values: FormValues) => ({
  paidDate: values.paidDate ? formatDate(values.paidDate, 'YYYY-MM-DD') : '',
  paymentDate: values.paymentDate
    ? formatDate(values.paymentDate, 'YYYY-MM-DD')
    : '',
});

// The estimate the request carries for a car rental (see
// CarRentalAmountState): the typed amount while it is an estimate, the saved
// estimate once final, nothing for an older rental that never had one.
const getEstimatedAmountCents = (
  values: FormValues,
  saved?: AccountingExpense
) => {
  const state = getCarRentalAmountState(saved);
  if (state === 'final') {
    return Number(saved?.rentalDetail?.estimatedAmountCents);
  }
  return state === 'estimate' ? formRentalCents(values) : undefined;
};

// Builds the `rentalDetail` key of the create/edit request, matching the
// server's three states (main/src/services/accounting.js):
//   - Car Rental selected -> the object (dollars -> integer cents; no
//     estimate is sent as undefined, never 0).
//   - Car Rental removed from an expense that had saved detail (edit) ->
//     explicit null, the only signal that deletes it.
//   - otherwise -> the key is omitted, which leaves saved detail untouched.
// `saved` is the expense being edited (none on create). Callers must drop
// the form-shaped `values.rentalDetail` from their own spread before
// applying this.
export const getRentalDetailPayload = (
  values: FormValues,
  saved?: AccountingExpense
) => {
  if (!isCarRentalSelected(values.categories)) {
    return saved?.rentalDetail ? { rentalDetail: null } : {};
  }
  const detail = values.rentalDetail;
  return {
    rentalDetail: {
      notes: detail.notes || undefined,
      renterName: detail.renterName.trim(),
      agreementNumber: detail.agreementNumber.trim() || undefined,
      estimatedAmountCents: getEstimatedAmountCents(values, saved),
      reservationNumber: detail.reservationNumber.trim() || undefined,
      rentalCompany:
        detail.rentalCompany as AccountingExpenseRentalDetail['rentalCompany'],
      bookingDate: detail.bookingDate
        ? formatDate(detail.bookingDate, 'YYYY-MM-DD')
        : undefined,
    },
  };
};

// The cent-exact split helpers live in utils/event-allocation.ts, shared with
// the stock transfer event split (T-L09); re-exported here unchanged.
export {
  calculateEventAmount,
  calculateEventAmounts,
  calculateEventPercents,
} from 'utils/event-allocation';
